// Data hooks for live weather / tides / AllTrails cache + the static trip
// JSON files (activities.json, drives.json). Each hook caches in-memory
// across components for the session via a module-scoped Map so the
// Today + Activities tabs don't double-fetch.
//
// Hook contract: a `key` uniquely identifies the resource (URL + params).
// The effect depends ONLY on `key` — not on the fetcher closure, which is
// rebuilt on every render. The latest fetcher is stashed in a ref so HMR
// /fast-refresh updates still take effect on subsequent key changes.

import { useEffect, useRef, useState } from 'react';

const memo = new Map();

function memoFetch(key, fetcher, ttlMs) {
  const now = Date.now();
  const hit = memo.get(key);
  if (hit && now - hit.at < ttlMs) return hit.promise;
  const promise = fetcher().catch((err) => {
    memo.delete(key);
    throw err;
  });
  memo.set(key, { promise, at: now });
  return promise;
}

function useAsync(key, fetcher, ttlMs) {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const lastKeyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    // Only flash a loading state when the key actually changed (initial
    // mount or genuine input change). A re-render with the same key
    // would otherwise wipe data each time.
    if (lastKeyRef.current !== key) {
      lastKeyRef.current = key;
      setState({ data: null, error: null, loading: true });
    }

    memoFetch(key, fetcherRef.current, ttlMs)
      .then((data) => {
        if (!cancelled) setState({ data, error: null, loading: false });
      })
      .catch((error) => {
        if (!cancelled) setState({ data: null, error, loading: false });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ttlMs]);

  return state;
}

async function getJson(url) {
  const res = await fetch(url, { credentials: 'omit' });
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  return res.json();
}

// ---- Live API hooks --------------------------------------------------

export function useWeather({ lat, lng, hours = 72, timezone = 'America/Juneau' }) {
  const key = `weather:${lat}:${lng}:${hours}:${timezone}`;
  const fetcher = () =>
    getJson(`/api/weather?lat=${lat}&lng=${lng}&hours=${hours}&timezone=${encodeURIComponent(timezone)}`);
  return useAsync(key, fetcher, 5 * 60 * 1000);
}

export function useTides({ hours = 72, station = '9452210' } = {}) {
  const key = `tides:${station}:${hours}`;
  const fetcher = () => getJson(`/api/tides?hours=${hours}&station=${station}`);
  return useAsync(key, fetcher, 30 * 60 * 1000);
}

export function useTrails() {
  const key = 'alltrails:default';
  const fetcher = () => getJson('/api/alltrails');
  return useAsync(key, fetcher, 60 * 60 * 1000);
}

// ---- Static trip-data hooks -----------------------------------------

export function useTripJson(tripId, file, { ttlMs = 60 * 60 * 1000 } = {}) {
  const key = `tripjson:${tripId}:${file}`;
  const fetcher = () => getJson(`/trips/${tripId}/${file}`);
  return useAsync(key, fetcher, ttlMs);
}

export function useActivities(tripId) {
  return useTripJson(tripId, 'activities.json');
}

export function useDrives(tripId) {
  return useTripJson(tripId, 'drives.json');
}
